import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';
import { useAuth } from '../../contexts/AuthContext';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const menuRef = useRef(null);

  const {
    notifications,
    unreadCount,
    loading,
    isLive,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useRealtimeNotifications(user?.id);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationIcon = (type) => {
    const iconMap = {
      'work_order_status_change': 'RefreshCw',
      'inspection_completed': 'CheckCircle',
      'quotation_approved': 'ThumbsUp',
      'quotation_rejected': 'ThumbsDown',
      'supplier_response': 'MessageSquare',
      'work_order_assigned': 'UserPlus',
      'work_order_completed': 'CheckCircle2'
    };
    return iconMap?.[type] || 'Bell';
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      'work_order_status_change': 'text-blue-600 bg-blue-50',
      'inspection_completed': 'text-green-600 bg-green-50',
      'quotation_approved': 'text-green-600 bg-green-50',
      'quotation_rejected': 'text-red-600 bg-red-50',
      'supplier_response': 'text-purple-600 bg-purple-50',
      'work_order_assigned': 'text-orange-600 bg-orange-50',
      'work_order_completed': 'text-teal-600 bg-teal-50'
    };
    return colorMap?.[type] || 'text-gray-600 bg-gray-50';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date?.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit' });
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.isRead) {
      await markAsRead(notification?.id);
    }

    if (notification?.workOrderId) {
      setIsOpen(false);
      navigate('/work-order-timeline', { state: { workOrderId: notification?.workOrderId } });
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e?.stopPropagation();
    await deleteNotification(notificationId);
  };

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'unread') return !notification?.isRead;
    return true;
  });

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <Icon name="Bell" className="w-6 h-6 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {isLive && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[600px] flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="Bell" className="w-5 h-5 text-gray-700" />
              <h3 className="text-sm font-semibold text-gray-900">Notificaciones</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          <div className="px-4 py-2 border-b border-gray-100 flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                filter === 'all' ?'bg-blue-100 text-blue-700' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                filter === 'unread' ?'bg-blue-100 text-blue-700' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              No leídas
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader2" className="w-6 h-6 text-blue-600 animate-spin" />
              </div>
            ) : filteredNotifications?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Inbox" size={48} className="text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {filter === 'unread' ? 'No hay notificaciones sin leer' : 'No hay notificaciones'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredNotifications?.map((notification) => (
                  <div
                    key={notification?.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification?.isRead ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${getNotificationColor(notification?.type)}`}>
                        <Icon
                          name={getNotificationIcon(notification?.type)}
                          size={16}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-gray-900">
                            {notification?.title}
                          </p>
                          {!notification?.isRead && (
                            <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mb-2">
                          {notification?.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Icon name="Clock" size={12} />
                            <span>{formatTime(notification?.createdAt)}</span>
                          </div>
                          <button
                            onClick={(e) => handleDeleteNotification(e, notification?.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            <Icon name="Trash2" size={12} className="text-gray-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {filteredNotifications?.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/work-order-timeline');
                }}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Ver todas las notificaciones
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
